import MarkDown from '@efdigital/ll-addon-content/components/mark-down';
import type PicklistsService from '@efdigital/ll-addon-content/services/picklists';
import type {
  CoachingPartnershipStatus,
  CoachProfile,
} from '@efdigital/ll-addon-data';
import { ToggleState } from '@efdigital/ll-addon-ui';
import {
  COACHING_PICKLIST_GROUPS,
  formatCertificationDates,
  isLastItem,
  languageProficiencyLabel,
} from '@efdigital/ll-xceed-v2/utils/coaching/coach-profile-display';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import { htmlSafe } from '@ember/template';
import Component from '@glimmer/component';

import { CoachProfileHeader } from './coach-profile-header.gts';
import CoachProfilePicklistBadges from './coach-profile-picklist-badges.gts';

interface CoachProfileSignature {
  Args: {
    coach: CoachProfile;
    partnershipStatus?: CoachingPartnershipStatus;
    relevance?: number;
  };
  Blocks: {
    actions: [];
  };
  Element: HTMLDivElement;
}

export default class CoachProfileView extends Component<CoachProfileSignature> {
  @service declare readonly picklists: PicklistsService;

  get showMatchBadge(): boolean {
    return (this.args.relevance ?? 0) > 0;
  }

  get showStats(): boolean {
    return (
      this.args.coach.yearsCoached != null ||
      this.args.coach.lifetimeSessionCount != null
    );
  }

  get showTypesAndSpecialties(): boolean {
    return (
      (this.args.coach.coachingTypes?.length ?? 0) > 0 ||
      (this.args.coach.specialties?.length ?? 0) > 0
    );
  }

  get showEducationCard(): boolean {
    return (
      (this.args.coach.education?.length ?? 0) > 0 ||
      (this.args.coach.certifications?.length ?? 0) > 0 ||
      (this.args.coach.assessments?.length ?? 0) > 0
    );
  }

  get videoEmbedHtml(): ReturnType<typeof htmlSafe> | null {
    const embed = this.args.coach.videoEmbed?.trim();
    return embed ? htmlSafe(embed) : null;
  }

  get hasAboutContent(): boolean {
    return Boolean(
      this.args.coach.bio?.trim() || this.args.coach.approachToCoaching?.trim()
    );
  }

  get showTypesSpecialtiesDivider(): boolean {
    return (
      (this.args.coach.coachingTypes?.length ?? 0) > 0 &&
      (this.args.coach.specialties?.length ?? 0) > 0
    );
  }

  get showEducationDividerBeforeCredentials(): boolean {
    return (
      (this.args.coach.education?.length ?? 0) > 0 &&
      (this.args.coach.certifications?.length ?? 0) > 0
    );
  }

  get showEducationDividerBeforeAssessments(): boolean {
    return (
      ((this.args.coach.education?.length ?? 0) > 0 ||
        (this.args.coach.certifications?.length ?? 0) > 0) &&
      (this.args.coach.assessments?.length ?? 0) > 0
    );
  }

  get hasYearsCoached(): boolean {
    return this.args.coach.yearsCoached != null;
  }

  get hasLifetimeSessionCount(): boolean {
    return this.args.coach.lifetimeSessionCount != null;
  }

  get statsYearsColumnClass(): string {
    return this.hasLifetimeSessionCount ? 'col-6' : 'col-12';
  }

  get statsSessionsColumnClass(): string {
    return this.hasYearsCoached ? 'col-6 border-start ps-3' : 'col-12';
  }

  languageLabel = (languageKey: string): string => {
    return (
      this.picklists.lookup(COACHING_PICKLIST_GROUPS.language, languageKey)
        ?.title ?? languageKey
    );
  };

  <template>
    <div ...attributes>
      <div class="mb-4">
        <CoachProfileHeader
          @coach={{@coach}}
          @partnershipStatus={{@partnershipStatus}}
          class="h-100"
        >
          <:actions>
            {{yield to="actions"}}
          </:actions>
        </CoachProfileHeader>

        {{#if this.videoEmbedHtml}}
          <div class="col-lg-4">
            <div
              class="card border-0 rounded-4 overflow-hidden shadow-sm h-100"
            >
              <div class="card-body p-4">
                <div class="gud-eyebrow d-flex align-items-center gap-2 mb-3">
                  <iconify-icon
                    inline
                    icon="ph:video-camera"
                    class="fs-5"
                    aria-hidden="true"
                  />
                  Video bio
                </div>
                <div class="ratio ratio-16x9">
                  {{this.videoEmbedHtml}}
                </div>
              </div>
            </div>
          </div>
        {{/if}}
      </div>

      <div class="row g-4">
        <div class="col-lg-8 d-flex flex-column gap-4">
          {{#if this.hasAboutContent}}
            <div class="card border shadow-sm rounded-4">
              <div class="card-body p-4 p-lg-5">
                <h2 class="gud-eyebrow mb-3">About me</h2>
                {{#if @coach.bio}}
                  <MarkDown
                    @markdown={{@coach.bio}}
                    class="mb-3 text-body lh-base"
                  />
                {{/if}}
                {{#if @coach.approachToCoaching}}
                  <ToggleState as |toggleState|>
                    {{#if toggleState.value}}
                      <h3 class="h6 fw-bold mb-2">Approach to coaching</h3>
                      <MarkDown
                        @markdown={{@coach.approachToCoaching}}
                        class="mb-3 text-body lh-base"
                      />
                    {{/if}}
                    <button
                      type="button"
                      class="btn btn-link link-primary text-decoration-none p-0 fw-semibold small d-inline-flex align-items-center gap-1 mb-0"
                      aria-expanded={{if toggleState.value "true" "false"}}
                      {{on "click" toggleState.toggle}}
                    >
                      {{if toggleState.value "Read less" "Read more"}}
                      <iconify-icon
                        inline
                        icon={{if
                          toggleState.value
                          "ph:caret-up"
                          "ph:caret-down"
                        }}
                        aria-hidden="true"
                      />
                    </button>
                  </ToggleState>
                {{/if}}
              </div>
            </div>
          {{/if}}

          {{#if this.showTypesAndSpecialties}}
            <div class="card border shadow-sm rounded-4">
              <div class="card-body p-4 p-lg-5">
                {{#if @coach.coachingTypes.length}}
                  <h2 class="gud-eyebrow mb-3">Coaching types</h2>
                  <CoachProfilePicklistBadges
                    @keys={{@coach.coachingTypes}}
                    @group={{COACHING_PICKLIST_GROUPS.coachingTypes}}
                    @variant="primary"
                    class="mb-0"
                  />
                {{/if}}

                {{#if this.showTypesSpecialtiesDivider}}
                  <hr class="my-4" />
                {{/if}}

                {{#if @coach.specialties.length}}
                  <h2 class="gud-eyebrow mb-3">Specialties</h2>
                  <CoachProfilePicklistBadges
                    @keys={{@coach.specialties}}
                    @group={{COACHING_PICKLIST_GROUPS.specialties}}
                    @variant="muted"
                  />
                {{/if}}
              </div>
            </div>
          {{/if}}

          {{#if this.showEducationCard}}
            <div class="card border shadow-sm rounded-4">
              <div class="card-body p-4 p-lg-5">
                {{#if @coach.education.length}}
                  <h2 class="gud-eyebrow mb-3">Education</h2>
                  {{#each @coach.education as |entry index|}}
                    <div
                      class="d-flex align-items-start gap-3
                        {{unless
                          (isLastItem index @coach.education.length)
                          'mb-4 pb-4 border-bottom'
                        }}"
                    >
                      <span
                        class="rounded-3 bg-body-secondary d-inline-flex align-items-center justify-content-center p-2 flex-shrink-0"
                      >
                        <iconify-icon
                          inline
                          icon="ph:graduation-cap"
                          class="fs-5 text-body-secondary"
                          aria-hidden="true"
                        />
                      </span>
                      <div>
                        <div class="fw-bold">{{entry.degree}}</div>
                        <div class="small text-body-secondary">
                          {{entry.institution}}
                          {{#if entry.yearOfGraduation}}
                            ·
                            {{entry.yearOfGraduation}}
                          {{/if}}
                        </div>
                      </div>
                    </div>
                  {{/each}}
                {{/if}}

                {{#if @coach.certifications.length}}
                  {{#if this.showEducationDividerBeforeCredentials}}
                    <hr class="my-4" />
                  {{/if}}
                  <h2 class="gud-eyebrow mb-3">Credentials</h2>
                  {{#each @coach.certifications as |cred index|}}
                    <div
                      class="d-flex align-items-start gap-3
                        {{unless
                          (isLastItem index @coach.certifications.length)
                          'mb-4 pb-4 border-bottom'
                        }}"
                    >
                      <span
                        class="rounded-3 bg-primary-subtle d-inline-flex align-items-center justify-content-center p-2 flex-shrink-0"
                      >
                        <iconify-icon
                          inline
                          icon="ph:medal"
                          class="fs-5 text-primary"
                          aria-hidden="true"
                        />
                      </span>
                      <div>
                        <div class="fw-bold">{{cred.title}}</div>
                        <div class="small text-body-secondary">
                          {{#if cred.governingBody}}
                            {{cred.governingBody}}
                            ·
                          {{/if}}
                          {{formatCertificationDates
                            cred.startDate
                            cred.endDate
                          }}
                        </div>
                      </div>
                    </div>
                  {{/each}}
                {{/if}}

                {{#if @coach.assessments.length}}
                  {{#if this.showEducationDividerBeforeAssessments}}
                    <hr class="my-4" />
                  {{/if}}
                  <h2 class="gud-eyebrow mb-3">Assessments</h2>
                  {{#each @coach.assessments as |assessment index|}}
                    <div
                      class="d-flex align-items-start gap-3
                        {{unless
                          (isLastItem index @coach.assessments.length)
                          'mb-4 pb-4 border-bottom'
                        }}"
                    >
                      <span
                        class="rounded-3 bg-warning-subtle d-inline-flex align-items-center justify-content-center p-2 flex-shrink-0"
                      >
                        <iconify-icon
                          inline
                          icon="ph:seal-check"
                          class="fs-5 text-warning-emphasis"
                          aria-hidden="true"
                        />
                      </span>
                      <div>
                        <div class="fw-bold">{{assessment.title}}</div>
                        <div class="small text-body-secondary">
                          {{#if assessment.governingBody}}
                            {{assessment.governingBody}}
                            ·
                          {{/if}}
                          {{formatCertificationDates
                            assessment.startDate
                            assessment.endDate
                          }}
                        </div>
                      </div>
                    </div>
                  {{/each}}
                {{/if}}
              </div>
            </div>
          {{/if}}
        </div>

        <div class="col-lg-4 d-flex flex-column gap-4">
          {{#if this.showStats}}
            <div class="card border shadow-sm rounded-4">
              <div class="card-body p-4">
                <div class="row g-3">
                  {{#if this.hasYearsCoached}}
                    <div class={{this.statsYearsColumnClass}}>
                      <div class="display-6 fw-bold lh-1 mb-2 text-primary">
                        {{@coach.yearsCoached}}
                      </div>
                      <div class="fw-semibold mb-0">years of experience</div>
                    </div>
                  {{/if}}
                  {{#if this.hasLifetimeSessionCount}}
                    <div class={{this.statsSessionsColumnClass}}>
                      <div class="display-6 fw-bold lh-1 mb-2 text-primary">
                        {{@coach.lifetimeSessionCount}}
                      </div>
                      <div class="fw-semibold mb-0">sessions delivered</div>
                    </div>
                  {{/if}}
                </div>
              </div>
            </div>
          {{/if}}

          {{#if @coach.languages.length}}
            <div class="card border shadow-sm rounded-4">
              <div class="card-body p-4 p-lg-5">
                <h2 class="gud-eyebrow mb-3">Languages</h2>
                {{#each @coach.languages as |lang index|}}
                  <div
                    class="d-flex justify-content-between align-items-center py-2
                      {{unless
                        (isLastItem index @coach.languages.length)
                        'border-bottom'
                      }}"
                  >
                    <span class="fw-semibold">{{this.languageLabel
                        lang.language
                      }}</span>
                    <span
                      class="badge rounded-pill bg-primary-subtle text-primary-emphasis fw-semibold small px-2 py-1"
                    >{{languageProficiencyLabel lang.proficiency}}</span>
                  </div>
                {{/each}}
              </div>
            </div>
          {{/if}}

          {{#if @coach.levelsOfSeniority.length}}
            <div class="card border shadow-sm rounded-4">
              <div class="card-body p-4 p-lg-5">
                <h2 class="gud-eyebrow mb-3">Target audience</h2>
                <CoachProfilePicklistBadges
                  @keys={{@coach.levelsOfSeniority}}
                  @group={{COACHING_PICKLIST_GROUPS.levelsOfSeniority}}
                  @variant="muted"
                />
              </div>
            </div>
          {{/if}}

          {{#if @coach.industries.length}}
            <div class="card border shadow-sm rounded-4">
              <div class="card-body p-4 p-lg-5">
                <h2 class="gud-eyebrow mb-3">Industries</h2>
                <CoachProfilePicklistBadges
                  @keys={{@coach.industries}}
                  @group={{COACHING_PICKLIST_GROUPS.industries}}
                  @variant="muted"
                />
              </div>
            </div>
          {{/if}}
        </div>
      </div>
    </div>
  </template>
}
